import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Send, Loader2, MapPin, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32"
      style={{ background: "var(--ll-bg-secondary)" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Info */}
          <div>
            <p
              className="text-xs tracking-[0.3em] uppercase mb-4"
              style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}
            >
              Get In Touch
            </p>
            <h2
              className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light mb-6"
              style={{ color: "var(--ll-text)" }}
            >
              Have{" "}
              <em className="italic" style={{ color: "var(--ll-text-secondary)" }}>
                questions
              </em>?
            </h2>
            <p
              className="text-base leading-relaxed mb-10"
              style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}
            >
              We'd love to hear from you. Whether you have a question about our products,
              want to collaborate, or just want to say hello — reach out.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail size={16} style={{ color: "var(--ll-green)" }} />
                <span className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
                  lemonadeliving4me@gmail.com
                </span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin size={16} style={{ color: "var(--ll-green)" }} />
                <span className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
                  South Africa
                </span>
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="space-y-5 p-8 rounded-2xl"
            style={{ background: "white", border: "1px solid var(--ll-border)" }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact-name" className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
                  Name *
                </Label>
                <Input
                  id="contact-name"
                  data-testid="contact-name-input"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  required
                  className="rounded-xl border bg-white/50 focus:bg-white"
                  style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email" className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
                  Email *
                </Label>
                <Input
                  id="contact-email"
                  data-testid="contact-email-input"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                  className="rounded-xl border bg-white/50 focus:bg-white"
                  style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-subject" className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
                Subject
              </Label>
              <Input
                id="contact-subject"
                data-testid="contact-subject-input"
                name="subject"
                value={form.subject}
                onChange={handleChange}
                placeholder="What's this about?"
                className="rounded-xl border bg-white/50 focus:bg-white"
                style={{ borderColor: "var(--ll-border)" }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-message" className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
                Message *
              </Label>
              <Textarea
                id="contact-message"
                data-testid="contact-message-input"
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Tell us what's on your mind..."
                required
                rows={5}
                className="rounded-xl border bg-white/50 focus:bg-white resize-none"
                style={{ borderColor: "var(--ll-border)" }}
              />
            </div>

            <button
              type="submit"
              data-testid="contact-submit-btn"
              disabled={loading}
              className="w-full rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {loading ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
